import type {
  ConflictDetailsFormData,
  RitoOption,
  RitoProcessoId,
  Template,
} from '@/features/processo-arbitral/types/conflictTypes';

type ConflictFieldErrors = Partial<Record<keyof ConflictDetailsFormData, string>>;

export const RITO_PROCESSO_DEFAULT: RitoProcessoId = 'sumarissimo';

export const defaultConflictDetailsFormData: ConflictDetailsFormData = {
  valorCausa: undefined,
  breveRelato: '',
  descricaoPedido: '',
  ritoProcesso: RITO_PROCESSO_DEFAULT,
  pedidoLiminar: false,
  descricaoUrgencia: '',
};

export const conflitoRitoOptions: RitoOption[] = [
  {
    id: 'sumarissimo',
    label: 'Sumaríssimo',
    descricao: 'Para conflitos de menor complexidade.',
    prazoMedio: '30 dias',
  },
  {
    id: 'sumario',
    label: 'Sumário',
    descricao: 'Para conflitos de média complexidade.',
    prazoMedio: '60 dias',
  },
  {
    id: 'ordinario',
    label: 'Ordinário',
    descricao: 'Para conflitos de maior complexidade.',
    prazoMedio: '90 dias',
  },
];

export const conflitoRelatoTemplates: Template[] = [
  {
    id: 'inadimplenciaContratual',
    titulo: 'Inadimplência Contratual',
    conteudo:
      'A parte Requerida deixou de cumprir a obrigação de pagamento assumida contratualmente, apesar das tentativas extrajudiciais de cobrança já realizadas. O inadimplemento persiste, gerando prejuízo financeiro direto à Requerente e inviabilizando a continuidade regular da relação contratual.',
  },
  {
    id: 'descumprimentoClausula',
    titulo: 'Descumprimento de Cláusula',
    conteudo:
      'As partes celebraram contrato contendo cláusulas específicas de cumprimento obrigatório. A Requerida deixou de observar obrigação expressamente prevista, causando desequilíbrio contratual e prejuízos concretos à Requerente, que busca a correta apuração das responsabilidades nesta arbitragem.',
  },
  {
    id: 'vicioProdutoServico',
    titulo: 'Vício no Produto ou Serviço',
    conteudo:
      'O produto ou serviço fornecido apresentou falhas relevantes, incompatíveis com a qualidade e a funcionalidade esperadas. Mesmo após comunicação formal à Requerida, o problema não foi resolvido adequadamente, o que motivou a instauração do presente procedimento arbitral.',
  },
  {
    id: 'rescisaoContratual',
    titulo: 'Rescisão Contratual',
    conteudo:
      'A relação contratual foi encerrada de forma irregular, sem observância das condições estabelecidas entre as partes. A conduta adotada pela Requerida provocou prejuízos materiais e operacionais, tornando necessária a submissão do conflito ao juízo arbitral.',
  },
];

export const conflitoPedidoTemplates: Template[] = [
  {
    id: 'cobrancaSimples',
    titulo: 'Cobrança Simples',
    conteudo:
      'Requer-se a condenação da Requerida ao pagamento integral do valor devido, acrescido de correção monetária, juros incidentes desde o inadimplemento e demais encargos aplicáveis ao caso.',
  },
  {
    id: 'obrigacaoFazer',
    titulo: 'Obrigação de Fazer',
    conteudo:
      'Requer-se que a Requerida seja compelida a cumprir a obrigação assumida contratualmente, em prazo a ser fixado pelo árbitro, sob pena de multa pelo descumprimento.',
  },
  {
    id: 'indenizacaoDanos',
    titulo: 'Indenização por Danos',
    conteudo:
      'Requer-se a condenação da Requerida ao pagamento de indenização pelos danos materiais e demais prejuízos comprovadamente suportados pela Requerente em razão dos fatos narrados.',
  },
  {
    id: 'rescisaoDevolucao',
    titulo: 'Rescisão e Devolução',
    conteudo:
      'Requer-se o reconhecimento da rescisão contratual por culpa da Requerida, com a devolução integral dos valores pagos e a apuração das perdas e danos cabíveis.',
  },
  {
    id: 'cumprimentoEspecifico',
    titulo: 'Cumprimento Específico',
    conteudo:
      'Requer-se que o árbitro determine o cumprimento específico da cláusula contratual descumprida, com imposição das medidas necessárias para assegurar a efetividade da decisão arbitral.',
  },
];

export const formatCurrencyValue = (value: number | undefined): string => {
  if (value === undefined) {
    return '';
  }

  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
};

export const parseCurrencyValue = (value: string): number | undefined => {
  const cleanValue = value.replace(/\D/g, '');

  if (!cleanValue) {
    return undefined;
  }

  return Number(cleanValue) / 100;
};

export const getSuggestedRitoProcesso = (valorCausa: number | undefined): RitoProcessoId | null => {
  if (!valorCausa || valorCausa <= 0) {
    return null;
  }

  if (valorCausa <= 50000) {
    return 'sumarissimo';
  }

  if (valorCausa <= 500000) {
    return 'sumario';
  }

  return 'ordinario';
};

export const getRitoProcessoHelpText = (
  ritoSugerido: RitoProcessoId | null,
  ritoSelecionado: RitoProcessoId | '',
): string => {
  if (!ritoSugerido) {
    return ritoSelecionado
      ? 'O rito está selecionado. Você pode trocar por outro.'
      : 'Escolha o procedimento adequado ao caso.';
  }

  if (ritoSelecionado === ritoSugerido) {
    return 'O rito sugerido está selecionado. Você pode trocar por outro.';
  }

  return 'O rito sugerido está destacado, mas a sua escolha prevalece.';
};

export const getRitoProcessoLabel = (ritoProcesso: RitoProcessoId | ''): string => {
  const option = conflitoRitoOptions.find((item) => item.id === ritoProcesso);

  return option?.label ?? '';
};

const validateConflictField = (
  field: keyof ConflictDetailsFormData,
  formData: ConflictDetailsFormData,
): string | undefined => {
  if (field === 'valorCausa') {
    if (formData.valorCausa === undefined || formData.valorCausa <= 0) {
      return 'O valor da causa é obrigatório';
    }

    return undefined;
  }

  if (field === 'breveRelato') {
    const text = formData.breveRelato.trim();

    if (!text) {
      return 'O relato é obrigatório';
    }

    if (text.length > 5000) {
      return 'O relato deve ter no máximo 5000 caracteres';
    }

    return undefined;
  }

  if (field === 'descricaoPedido') {
    const text = formData.descricaoPedido.trim();

    if (!text) {
      return 'O pedido é obrigatório';
    }

    if (text.length > 5000) {
      return 'O pedido deve ter no máximo 5000 caracteres';
    }

    return undefined;
  }

  if (field === 'ritoProcesso') {
    if (!formData.ritoProcesso) {
      return 'Selecione um rito processual';
    }

    return undefined;
  }

  if (field === 'descricaoUrgencia') {
    if (!formData.pedidoLiminar) {
      return undefined;
    }

    const text = formData.descricaoUrgencia.trim();

    if (!text) {
      return 'Descreva o motivo da urgência';
    }

    if (text.length > 1000) {
      return 'A descrição da urgência deve ter no máximo 1000 caracteres';
    }
  }

  return undefined;
};

export const validateConflictFormData = (
  formData: ConflictDetailsFormData,
): ConflictFieldErrors => {
  const errors: ConflictFieldErrors = {};
  const fields: Array<keyof ConflictDetailsFormData> = [
    'valorCausa',
    'breveRelato',
    'descricaoPedido',
    'ritoProcesso',
  ];

  fields.forEach((field) => {
    const error = validateConflictField(field, formData);

    if (error) {
      errors[field] = error;
    }
  });

  return errors;
};

export const isConflictFormValid = (formData: ConflictDetailsFormData): boolean =>
  Object.keys(validateConflictFormData(formData)).length === 0;
